import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { useSmoothScroll } from "@/hooks/use-smooth-scroll";
import { DURATION } from "@/lib/animations";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, HelpCircle, MessageCircle } from "lucide-react";
import { useState } from "react";

// FAQ data with categories
const faqData = {
  general: {
    title: { en: "General", ar: "عام" },
    questions: [
      {
        q: { en: "What services does Ibtkary offer?", ar: "ما هي الخدمات التي تقدمها ابتكاري؟" },
        a: {
          en: "We offer a comprehensive range of digital solutions including mobile app development (Flutter), backend development (Go), web development (React), UI/UX design, and technical consulting.",
          ar: "نقدم مجموعة شاملة من الحلول الرقمية تشمل تطوير تطبيقات الموبايل (Flutter)، تطوير الباك إند (Go)، تطوير الويب (React)، تصميم واجهة المستخدم، والاستشارات التقنية."
        },
      },
      {
        q: { en: "How long does a typical project take?", ar: "كم يستغرق المشروع النموذجي؟" },
        a: {
          en: "Project timelines vary based on complexity and scope. A simple mobile app might take 4-8 weeks, while more complex systems can take 3-6 months. We provide detailed timelines after understanding your requirements.",
          ar: "تختلف الجداول الزمنية للمشاريع بناءً على التعقيد والنطاق. قد يستغرق تطبيق موبايل بسيط 4-8 أسابيع، بينما قد تستغرق الأنظمة الأكثر تعقيداً 3-6 أشهر. نقدم جداول زمنية مفصلة بعد فهم متطلباتك."
        },
      },
    ],
  },
  services: {
    title: { en: "Services", ar: "الخدمات" },
    questions: [
      {
        q: { en: "Do you develop cross-platform mobile apps?", ar: "هل تطورون تطبيقات موبايل متعددة المنصات؟" },
        a: {
          en: "Yes! We specialize in Flutter development, which allows us to create beautiful, natively compiled applications for iOS, Android, and desktop from a single codebase, saving time and costs.",
          ar: "نعم! نحن متخصصون في تطوير Flutter، مما يتيح لنا إنشاء تطبيقات جميلة ومجمعة محلياً لـ iOS و Android وسطح المكتب من قاعدة كود واحدة، مما يوفر الوقت والتكاليف."
        },
      },
      {
        q: { en: "Can you help with existing projects?", ar: "هل يمكنكم المساعدة في المشاريع القائمة؟" },
        a: {
          en: "Absolutely! We offer maintenance, optimization, and feature enhancement services for existing applications. Our team can review your codebase and suggest improvements.",
          ar: "بالتأكيد! نقدم خدمات الصيانة والتحسين وإضافة الميزات للتطبيقات القائمة. يمكن لفريقنا مراجعة الكود الخاص بك واقتراح التحسينات."
        },
      },
    ],
  },
  pricing: {
    title: { en: "Pricing", ar: "التسعير" },
    questions: [
      {
        q: { en: "How do you price your projects?", ar: "كيف تسعرون مشاريعكم؟" },
        a: {
          en: "We offer flexible pricing models including fixed-price for well-defined projects and time & materials for evolving requirements. Contact us for a free consultation and quote.",
          ar: "نقدم نماذج تسعير مرنة تشمل السعر الثابت للمشاريع المحددة جيداً والوقت والمواد للمتطلبات المتطورة. تواصل معنا للحصول على استشارة مجانية وعرض أسعار."
        },
      },
      {
        q: { en: "Do you offer payment plans?", ar: "هل تقدمون خطط دفع؟" },
        a: {
          en: "Yes, we typically structure payments in milestones aligned with project phases. This ensures transparency and allows you to track progress before each payment.",
          ar: "نعم، عادةً ما ننظم المدفوعات على شكل معالم متوافقة مع مراحل المشروع. هذا يضمن الشفافية ويتيح لك تتبع التقدم قبل كل دفعة."
        },
      },
    ],
  },
  support: {
    title: { en: "Support", ar: "الدعم" },
    questions: [
      {
        q: { en: "What support do you provide after launch?", ar: "ما الدعم الذي تقدمونه بعد الإطلاق؟" },
        a: {
          en: "We offer various post-launch support packages including bug fixes, security updates, feature additions, and 24/7 monitoring for critical systems. We're committed to your long-term success.",
          ar: "نقدم حزم دعم متنوعة بعد الإطلاق تشمل إصلاح الأخطاء، تحديثات الأمان، إضافة الميزات، والمراقبة على مدار الساعة للأنظمة الحرجة. نحن ملتزمون بنجاحك على المدى الطويل."
        },
      },
      {
        q: { en: "How can I contact your support team?", ar: "كيف يمكنني التواصل مع فريق الدعم؟" },
        a: {
          en: "You can reach us via email at info@Ibtkary.net, phone at +201068338151, or through the contact form on our website. We typically respond within 24 hours.",
          ar: "يمكنك التواصل معنا عبر البريد الإلكتروني info@Ibtkary.net، الهاتف +201068338151، أو من خلال نموذج الاتصال على موقعنا. نرد عادةً خلال 24 ساعة."
        },
      },
    ],
  },
};

type CategoryKey = keyof typeof faqData;

function FAQItem({
  question,
  answer,
  isOpen,
  onToggle,
  isRTL,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  isRTL: boolean;
}) {
  return (
    <div className="border-b border-border/50 last:border-0">
      <button
        onClick={onToggle}
        className={`w-full py-5 flex items-center justify-between gap-4 hover:text-primary transition-colors ${isRTL ? 'text-right' : 'text-left'}`}
        style={{ direction: isRTL ? 'rtl' : 'ltr' }}
      >
        <span className="font-medium text-foreground">{question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: DURATION.fast }}
          className="flex-shrink-0"
        >
          <ChevronDown className="h-5 w-5 text-muted-foreground" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: DURATION.normal }}
            className="overflow-hidden"
          >
            <p
              className={`pb-5 text-muted-foreground leading-relaxed ${isRTL ? 'text-right' : ''}`}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function FAQ() {
  const { language } = useLanguage();
  const scrollTo = useSmoothScroll();
  const isRTL = language === 'ar';
  const [activeCategory, setActiveCategory] = useState<CategoryKey>("general");
  const [openQuestion, setOpenQuestion] = useState<number | null>(0);

  const categories = Object.keys(faqData) as CategoryKey[];

  return (
    <section id="faq" className="py-24 bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow }}
          viewport={{ once: true }}
        >
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 text-primary mb-6">
            <HelpCircle className="h-7 w-7" />
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {isRTL ? (
              <>الأسئلة <span className="gradient-text">الشائعة</span></>
            ) : (
              <>Frequently Asked <span className="gradient-text">Questions</span></>
            )}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {isRTL
              ? "إجابات على الأسئلة الأكثر شيوعاً حول خدماتنا وطريقة عملنا"
              : "Answers to the most common questions about our services and how we work"}
          </p>
        </motion.div>

        {/* Category Tabs */}
        <motion.div
          className="flex flex-wrap justify-center gap-2 mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow, delay: 0.1 }}
          viewport={{ once: true }}
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setActiveCategory(category);
                setOpenQuestion(0);
              }}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeCategory === category
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {faqData[category].title[language]}
            </button>
          ))}
        </motion.div>

        {/* FAQ List */}
        <motion.div
          className="bg-card rounded-2xl border border-border/50 p-6 md:p-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, x: isRTL ? -20 : 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isRTL ? 20 : -20 }}
              transition={{ duration: DURATION.fast }}
            >
              {faqData[activeCategory].questions.map((item, index) => (
                <FAQItem
                  key={index}
                  question={item.q[language]}
                  answer={item.a[language]}
                  isOpen={openQuestion === index}
                  onToggle={() => setOpenQuestion(openQuestion === index ? null : index)}
                  isRTL={isRTL}
                />
              ))}
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow, delay: 0.3 }}
          viewport={{ once: true }}
        >
          <p className="text-muted-foreground mb-4">
            {isRTL ? "لا زال لديك سؤال؟" : "Still have a question?"}
          </p>
          <Button
            onClick={() => scrollTo("#contact")}
            className="group"
          >
            <MessageCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
            {isRTL ? "تواصل معنا" : "Contact Us"}
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
